import React, {
  Dispatch, Ref, SetStateAction, useEffect, useState,
} from 'react';
import {
  StyleSheet, View, Text, FlatList, ActivityIndicator,
} from 'react-native';
import ViewShot from 'react-native-view-shot';
import ButtonTile from './ButtonTile';
import Colors from '../../../constants/Colors';
import { DateRange } from '../../../redux/dateRangeSlice';
import GetTicketsRaisedCounts from '../../../requests/SolveAnalytics/GetTicketsRaisedCounts';
import { Template } from '../../../constants/Types';
import { getPreviousDateRange } from '../../../constants/Utils';
import asNumber from '../../../components/AsNumber';

interface SelectedTemplatesProps {
  selectedTemplates: Template[] | null,
  range: DateRange,
  compareRange: DateRange,
  flatListRef: Ref<FlatList>,
  buildingId: string,
  viewShotRef?: Ref<ViewShot>,
}

const SelectedTemplates = ({
  selectedTemplates, range, compareRange, flatListRef, buildingId, viewShotRef,
}: SelectedTemplatesProps) => {
  const [currentCounts, setCurrentCounts] = useState<number[]>([]);
  const [previousCounts, setPreviousCounts] = useState<number[]>([]);
  const [comparisonCounts, setComparisonCounts] = useState<number[]>([]);

  const saveCounts = (dateRange: DateRange, setCounts: Dispatch<SetStateAction<number[]>>) => {
    if (!selectedTemplates) return;
    const results = Promise.all(selectedTemplates
      .map((template: Template) => GetTicketsRaisedCounts({
        range: dateRange,
        buildingId,
        templateId: template.id,
      })));
    results.then((outputs) => {
      setCounts(outputs.map((output) => output.ticketsRaisedCounts
        .reduce((total: number, { value }) => total + (value ?? 0), 0)));
    });
  };

  useEffect(() => {
    saveCounts(range, setCurrentCounts);
    saveCounts(getPreviousDateRange(range), setPreviousCounts);
  }, [range, selectedTemplates]);

  useEffect(() => {
    saveCounts(compareRange, setComparisonCounts);
  }, [compareRange, selectedTemplates]);

  return <ViewShot ref={viewShotRef}>
    <View
      style={styles.container}
      testID='template-cards-modal'
    >
      <FlatList
        ref={flatListRef}
        horizontal
        contentContainerStyle={styles.listContent}
        data={selectedTemplates}
        renderItem={({ item, index }) => <ButtonTile
          key={`button_tile_${index}`}
          title={item.name}
          value={currentCounts[index] ?? 0}
          compareValue={comparisonCounts[index] ?? 0}
          change={asNumber(100 * ((currentCounts[index] - previousCounts[index])
            / previousCounts[index])) ?? 0}
          isMobile={false}
          compareStart={compareRange.start}
          testID={index}
        />}
        ListEmptyComponent={selectedTemplates ? <Text style={styles.noTemplatesText}>
          {'No templates selected.\nSelect some templates by clicking the edit button'}
        </Text> : <ActivityIndicator />}
        keyExtractor={(_, index: number) => `template_tile_${index}`}
        onScrollToIndexFailed={() => { }}
      />
    </View>
  </ViewShot>;
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  listContent: {
    paddingEnd: 12,
  },
  noTemplatesText: {
    marginTop: 24,
    marginBottom: 8,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
  },
});

export default SelectedTemplates;
